import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FiFileText, FiUploadCloud, FiUserPlus, FiEye, FiEyeOff } from "react-icons/fi";
import { motion } from "framer-motion";
import api from "../services/api";

const Register = () => {
  const [gender, setGender] = useState("");
  const [idType, setIdType] = useState("");
  const [civilStatusId, setCivilStatusId] = useState("");
  const [nationalityId, setNationalityId] = useState("");
  const [idFileName, setIdFileName] = useState("");
  const idInputRef = useRef(null);
  const [idTypes, setIdTypes] = useState([]);
  const [civilStatuses, setCivilStatuses] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("referral_code") || "";
  const [referralInput, setReferralInput] = useState(referralCode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [submitErrors, setSubmitErrors] = useState([]);
  const [successData, setSuccessData] = useState(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [guardianName, setGuardianName] = useState("");
  const [relationshipToPrincipal, setRelationshipToPrincipal] = useState("");
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const [policyContent, setPolicyContent] = useState("Loading policy...");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setReferralInput(referralCode);
  }, [referralCode]);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [idTypeRes, civilRes, nationalityRes] = await Promise.all([
          api.get("/id-types"),
          api.get("/civil-statuses"),
          api.get("/nationalities"),
        ]);

        if (idTypeRes.data?.status === "success") {
          setIdTypes(idTypeRes.data?.data || []);
        }
        if (civilRes.data?.status === "success") {
          setCivilStatuses(civilRes.data?.data || []);
        }
        if (nationalityRes.data?.status === "success") {
          setNationalities(nationalityRes.data?.data || []);
        }
      } catch (error) {
        return;
      }
    };

    fetchLookups();
  }, []);

  useEffect(() => {
    const loadPolicy = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL || ""}/dataprivacypolicy.html`);
        if (!response.ok) {
          setPolicyContent("Failed to load policy content.");
          return;
        }
        const text = await response.text();
        setPolicyContent(text || "No policy content available.");
      } catch (error) {
        setPolicyContent("Failed to load policy content.");
      }
    };
    loadPolicy();
  }, []);

  const isMinor = useMemo(() => {
    if (!dateOfBirth) {
      return false;
    }
    const birthDate = new Date(dateOfBirth);
    if (Number.isNaN(birthDate.getTime())) {
      return false;
    }
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age -= 1;
    }
    return age <= 17;
  }, [dateOfBirth]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitSuccess("");
      setSubmitErrors([]);
      setIsErrorModalOpen(false);

      try {
        if (!referralInput) {
        setSubmitErrors(["Referral code is required."]);
        setIsErrorModalOpen(true);
        return;
      }

      const file = idInputRef.current?.files?.[0];
      if (!file) {
        setSubmitErrors(["ID file is required."]);
        setIsErrorModalOpen(true);
        return;
      }

      if (!termsAgreed) {
        setSubmitErrors(["You must agree to the Data Privacy Policy and Terms & Conditions."]);
        setIsErrorModalOpen(true);
        return;
      }

      if (isMinor && (!guardianName.trim() || !relationshipToPrincipal.trim())) {
        setSubmitErrors([
          "Name of Guardian and Relationship to Principal are required for minors.",
        ]);
        setIsErrorModalOpen(true);
        return;
      }

      const formData = new FormData(event.currentTarget);
      formData.set("referral_code", referralInput);
      formData.set("id_file", file);
      formData.set("data_privacy_terms_agreed", "1");
      if (isMinor) {
        formData.set("guardian_name", guardianName.trim());
        formData.set("relationship_to_principal", relationshipToPrincipal.trim());
      }
      const response = await api.post("/register", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data?.status !== "success") {
        const message = response.data?.message || "Registration failed.";
        setSubmitErrors([message]);
        setIsErrorModalOpen(true);
        return;
      }

      setSubmitErrors([]);
      setIsErrorModalOpen(false);
      setSuccessData(response.data?.data || null);
      setIsSuccessModalOpen(true);
      setSubmitSuccess("Registration successful.");
      event.currentTarget.reset();
      setIdFileName("");
      setGender("");
      setIdType("");
      setCivilStatusId("");
      setNationalityId("");
      setMobileNumber("");
      setDateOfBirth("");
      setGuardianName("");
      setRelationshipToPrincipal("");
      if (!referralCode) {
        setReferralInput("");
      }
    } catch (error) {
      const message = error?.response?.data?.message;
      if (message && typeof message === "object") {
        const flattened = Object.values(message).flat();
        setSubmitErrors(flattened);
      } else if (typeof message === "string") {
        setSubmitErrors([message]);
      } else {
        setSubmitErrors(["Registration failed. Please try again."]);
      }
      setIsErrorModalOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen bg-[linear-gradient(135deg,#e0f2fe_0%,#fce7f3_55%,#fed7aa_100%)] px-6 py-10"
    >
      <div className="mx-auto flex w-full max-w-[900px] flex-col gap-6">
        <header className="flex items-center gap-3 rounded-3xl border border-white/60 bg-white/90 px-6 py-4 shadow-lg shadow-slate-200/60">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,var(--brand-blue)_0%,var(--brand-blue-2)_100%)] text-white">
            <FiUserPlus />
          </div>
          <div className="text-lg font-semibold text-slate-900">
            RMC2 Emergency Care (ER)
          </div>
          <Link
            to="/login"
            className="ml-auto text-sm font-semibold text-[var(--brand-blue)] hover:text-[var(--brand-blue-2)]"
          >
            Back to Login
          </Link>
        </header>

        <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-100 text-[var(--brand-blue)]">
              <FiFileText />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                Referral
              </p>
              <h2 className="text-2xl font-semibold text-slate-900">
                Referral Info
              </h2>
            </div>
          </div>
          <div className="mt-5 flex flex-col gap-2">
            <label className="text-xs font-semibold text-slate-500">
              Referral Code
            </label>
            <input
              type="text"
              placeholder="Referral Code"
              value={referralInput}
              onChange={(event) => setReferralInput(event.target.value)}
              readOnly={!!referralCode}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          encType="multipart/form-data"
          className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60"
        >
          <input
            type="hidden"
            name="referral_code"
            value={referralInput}
            readOnly
          />
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-100 text-[var(--brand-blue)]">
              <FiUserPlus />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                Personal
              </p>
              <h2 className="text-2xl font-semibold text-slate-900">
                Personal Information
              </h2>
            </div>
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-4">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Last Name
              </label>
              <input
                type="text"
                placeholder="Last Name"
                name="last_name"
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                First Name
              </label>
              <input
                type="text"
                placeholder="First Name"
                name="first_name"
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Middle Name
              </label>
              <input
                type="text"
                placeholder="Middle Name"
                name="middle_name"
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Suffix
              </label>
              <input
                type="text"
                placeholder="Suffix"
                name="suffix"
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Date of Birth
              </label>
              <input
                type="date"
                name="date_of_birth"
                value={dateOfBirth}
                onChange={(event) => setDateOfBirth(event.target.value)}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Gender
              </label>
              <select
                value={gender}
                onChange={(event) => setGender(event.target.value)}
                name="gender"
                className={`rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  gender ? "text-slate-700" : "text-slate-400"
                }`}
              >
                <option value="">Gender</option>
                <option value="male" className="text-slate-700">
                  Male
                </option>
                <option value="female" className="text-slate-700">
                  Female
                </option>
              </select>
            </div>
          </div>

          {isMinor && (
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Name of Guardian
                </label>
                <input
                  type="text"
                  name="guardian_name"
                  value={guardianName}
                  onChange={(event) => setGuardianName(event.target.value)}
                  placeholder="Name of Guardian"
                  required={isMinor}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Relationship to Principal
                </label>
                <input
                  type="text"
                  name="relationship_to_principal"
                  value={relationshipToPrincipal}
                  onChange={(event) => setRelationshipToPrincipal(event.target.value)}
                  placeholder="Relationship to Principal"
                  required={isMinor}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>
          )}

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Civil Status
              </label>
              <select
                value={civilStatusId}
                onChange={(event) => setCivilStatusId(event.target.value)}
                name="civil_status_id"
                className={`rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  civilStatusId ? "text-slate-700" : "text-slate-400"
                }`}
              >
                <option value="">Civil Status</option>
                {civilStatuses.map((status) => (
                  <option key={status.id} value={status.id} className="text-slate-700">
                    {status.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Nationality
              </label>
              <select
                value={nationalityId}
                onChange={(event) => setNationalityId(event.target.value)}
                name="nationality_id"
                className={`rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  nationalityId ? "text-slate-700" : "text-slate-400"
                }`}
              >
                <option value="">Nationality</option>
                {nationalities.map((nationality) => (
                  <option
                    key={nationality.id}
                    value={nationality.id}
                    className="text-slate-700"
                  >
                    {nationality.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Home Address
              </label>
              <textarea
                name="home_address"
                rows={3}
                placeholder="Home Address"
                className="resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                ID Type
              </label>
              <select
                value={idType}
                onChange={(event) => setIdType(event.target.value)}
                name="id_type_id"
                className={`rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  idType ? "text-slate-700" : "text-slate-400"
                }`}
              >
                <option value="">ID Type</option>
                {idTypes.map((type) => (
                  <option key={type.id} value={type.id} className="text-slate-700">
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Upload ID
              </label>
              <button
                type="button"
                onClick={() => idInputRef.current?.click()}
                className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-500 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-500"
              >
                <FiUploadCloud />
                {idFileName ? idFileName : "Upload ID"}
              </button>
            </div>
          </div>

          <input
            ref={idInputRef}
            type="file"
            name="id_file"
            className="hidden"
            accept="image/*,.pdf"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                setIdFileName(file.name);
              }
            }}
          />

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Mobile Number
              </label>
              <div className="flex items-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-200">
                <span className="text-slate-400">+63</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={10}
                  placeholder="Enter 10 digits"
                  name="mobile_number"
                  value={mobileNumber}
                  onChange={(event) => {
                    const digits = event.target.value.replace(/\D/g, "");
                    setMobileNumber(digits.slice(0, 10));
                  }}
                  className="ml-2 w-full border-0 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Email Address
              </label>
              <input
                type="email"
                placeholder="Email Address"
                name="email"
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>

          <div className="mt-4">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Username
              </label>
              <input
                type="text"
                placeholder="Username"
                name="username"
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  name="password"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-12 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-slate-500">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  name="confirm_password"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-12 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-start gap-2 text-sm text-slate-600">
            <input
              id="termsAgreed"
              type="checkbox"
              checked={termsAgreed}
              onChange={(event) => setTermsAgreed(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-[var(--brand-orange)] focus:ring-[var(--brand-orange)]"
            />
            <label htmlFor="termsAgreed" className="flex-1">
              I have read and agree to the{" "}
              <button
                type="button"
                onClick={() => setIsPolicyModalOpen(true)}
                className="font-semibold text-[var(--brand-blue)] underline decoration-dotted underline-offset-4"
              >
                Data Privacy Policy and Terms & Conditions
              </button>
              .
            </label>
          </div>

          <div className="mt-8 flex flex-wrap justify-end gap-3">
            {/* <button
              type="button"
              className="rounded-2xl border border-[var(--brand-orange)] px-6 py-3 text-sm font-semibold text-[var(--brand-orange)] transition hover:bg-orange-50"
            >
              Cancel
            </button> */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-2xl bg-[var(--brand-orange)] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
            >
              {isSubmitting ? "Submitting..." : "Create Account"}
            </button>
          </div>
          
        </form>
      </div>
      {isSuccessModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-500">
                  Success
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Registration Complete
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Keep these details for your records and verify your email to activate your account.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsSuccessModalOpen(false);
                  navigate("/email-verification", {
                    state: { email: successData?.email },
                  });
                }}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 space-y-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 text-sm text-slate-700">
              <div className="flex items-center justify-between gap-4">
                <span className="text-slate-500">Username</span>
                <span className="font-semibold text-slate-900">
                  {successData?.username || "-"}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-slate-500">Full Name</span>
                <span className="font-semibold text-slate-900">
                  {successData?.first_name || "-"} {successData?.last_name || ""}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-slate-500">Email</span>
                <span className="font-semibold text-slate-900">
                  {successData?.email || "-"}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-slate-500">Referral Code</span>
                <span className="font-semibold text-slate-900">
                  {successData?.referral_code || "-"}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-slate-500">Upload ID</span>
                <span className="font-semibold text-slate-900">
                  {successData?.upload_id || "-"}
                </span>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setIsSuccessModalOpen(false);
                  navigate("/email-verification", {
                    state: { email: successData?.email },
                  });
                }}
                className="rounded-2xl bg-emerald-500 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-200 hover:bg-emerald-600"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
      {isErrorModalOpen && submitErrors.length > 0 && !isSuccessModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-rose-500">
                  Error
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Registration Failed
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Please review the issues below and try again.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsErrorModalOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 space-y-3 rounded-2xl border border-rose-100 bg-rose-50/60 p-4 text-sm text-rose-700">
              <ul className="space-y-2">
                {submitErrors.map((item, index) => (
                  <li key={`${item}-${index}`} className="flex gap-2">
                    <span className="mt-1 h-2 w-2 rounded-full bg-rose-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsErrorModalOpen(false)}
                className="rounded-2xl bg-rose-500 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-rose-200 hover:bg-rose-600"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
      {isPolicyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-4xl rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Policy
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Data Privacy Policy & Terms
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsPolicyModalOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 h-[500px] overflow-hidden rounded-2xl border border-slate-200">
              <iframe
                title="Data Privacy Policy"
                srcDoc={policyContent}
                className="h-full w-full rounded-2xl"
              />
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsPolicyModalOpen(false)}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Register;
